<template>
    <div class="IssueForm">
        <div class="Form mb-2">
            <div
                v-if="(value || {}).message"
                class="Field mb-1"
            >
                <Issue v-bind="value" />
            </div>

            <div
                v-if="!isSelect && !isConflict"
                class="Field mb-1"
            >
                <WText
                    tag="label"
                    typo="label"
                    weight="semi"
                    :size="14"
                    uppercase
                    class="mb-1"
                >
                    {{ $t('imports.create.issues.form.currentValue.label') }}
                </WText>
                <WText
                    tag="p"
                    typo="p"
                    :size="16"
                >
                    "{{ currentValue }}"
                </WText>
            </div>

            <Tabs
                v-if="tabs.length > 1"
                v-model="activeTab"
                :tabs="tabs"
                class="mb-1"
            />

            <FadeTransition mode="out-in">
                <div
                    v-if="activeTab === tabs[0].key"
                    :key="activeTab"
                    class="Field mb-1"
                >
                    <WText
                        tag="label"
                        typo="label"
                        weight="semi"
                        :size="14"
                        uppercase
                        class="mb-1"
                    >
                        {{ $t('imports.create.issues.form.value.label') }}
                    </WText>

                    <div class="FormCol">
                        <div v-if="isConflict">
                            <div class="Option">
                                <WRadio
                                    :modelValue="conflictChoice"
                                    value="saved"
                                    name="conflictChoice"
                                    :label="`Keep saved: ${formatValue(savedValue)}`"
                                    @input="handleConflictChange"
                                />
                            </div>
                            <div class="Option">
                                <WRadio
                                    :modelValue="conflictChoice"
                                    value="new"
                                    name="conflictChoice"
                                    :label="`Use imported: ${formatValue(newValue)}`"
                                    @input="handleConflictChange"
                                />
                            </div>
                        </div>
                        <div v-else-if="isSelect">
                            <div
                                v-for="({ label, value: optionValue }, index) in value.options"
                                :key="index"
                                class="Option"
                                :class="{ 'Separator': [optionsCustom.SEARCH, optionsCustom.NEW, optionsCustom.NONE].includes(optionValue) }"
                            >
                                <div class="flex">
                                    <WRadio
                                        :modelValue="option"
                                        :value="optionValue"
                                        :name="label"
                                        :label="label"
                                        @input="handleOptionChange"
                                    />
                                    <WSelect
                                        v-if="optionsCustom.SEARCH === optionValue"
                                        name="search"
                                        :value="search"
                                        :options="searchOptions"
                                        :placeholder="$t('actions.search')"
                                        :disabled="!isSearchable"
                                        :loading="isSearching"
                                        :internalSearch="internalSearch"
                                        searchable
                                        class="Input flex-1"
                                        @search="getSearchOptions"
                                        @input="handleSearchChange"
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            v-else
                            class="Input"
                        >
                            <template v-if="fieldInputs[value.type] === inputs.BOOL">
                                <WSwitch
                                    :value="inputValue"
                                    name="value"
                                    :disabled="loading"
                                    @input="handleInputChange"
                                />
                            </template>
                            <template v-else-if="isDate">
                                <WDatepicker
                                    :value="inputValue"
                                    :placeholder="datePlaceholder"
                                    :formats="dateFormats"
                                    name="value"
                                    :disabled="loading"
                                    @input="handleInputChange"
                                />
                            </template>
                            <template v-else>
                                <WInput
                                    :value="inputValue"
                                    :placeholder="$t('imports.create.issues.form.value.placeholder')"
                                    name="value"
                                    type="text"
                                    :disabled="loading"
                                    @input="handleInputChange"
                                />
                            </template>
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    :key="activeTab"
                >
                    <Sources
                        v-model="mappers"
                        :isLoading="loading"
                        class="mb-2"
                    />
                </div>
            </FadeTransition>
        </div>
    </div>
</template>
<script>
/* eslint-disable no-underscore-dangle */
import VueTypes from 'vue-types'
import {
    Api, apiRequest, rethrowError, notifyError,
} from '@/api'
import { INPUTS, FIELD_INPUTS } from '@/entities/fields'
import { Tabs, Sources, Issue } from '@/components'
import {
    DATE_PICKER_FORMATS, DATE_PLACEHOLDER, formatDate, toPickerDate, toApiDate,
} from './dates'

const CUSTOM_OPTIONS = {
    SEARCH: 'search',
    NEW: 'new',
    NONE: 'none',
}

// TODO: Enable transforms after create services in back-end
const ENABLE_NORMALIZATION = false

export default {
    name: 'IssueForm',
    components: {
        Tabs,
        Sources,
        Issue,
    },
    props: {
        value: VueTypes.shape({
            type: VueTypes.string,
            level: VueTypes.string,
            message: VueTypes.string,
            row: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
            field: VueTypes.string,
        }).loose,
        defaultValue: VueTypes.any,
        loading: VueTypes.bool,
    },
    data () {
        const tabs = [
            {
                key: 'edit',
                text: this.$t('imports.create.issues.edit'),
            },
            ...(ENABLE_NORMALIZATION ? [{
                key: 'normalization',
                text: this.$t('imports.create.issues.normalization'),
            }] : []),
        ]

        return {
            inputValue: '',
            currentValue: '',

            option: '',
            conflictChoice: '',
            search: '',
            searchOptions: [],
            searchConfig: undefined,
            internalSearch: false,
            isSearching: false,
            mappers: {},

            tabs,
            activeTab: tabs[0].key,
        }
    },
    computed: {
        isSelect () {
            const { type, options } = this.value || {}
            return !!(FIELD_INPUTS[type] === INPUTS.SELECT && (options || []).length)
        },
        isSearchable () {
            return this.isSelect && this.option === CUSTOM_OPTIONS.SEARCH
        },
        isConflict () {
            return (this.value || {}).type === 'conflict'
        },
        savedValue () {
            return (this.value || {}).saved
        },
        newValue () {
            return (this.value || {}).new
        },
        isDate () {
            const { fieldType, type } = this.value || {}
            return FIELD_INPUTS[fieldType || type] === INPUTS.DATE
        },
    },
    watch: {
        'value.id': {
            handler () {
                this.initialize()
            },
        },
    },
    beforeCreate () {
        this.inputs = INPUTS
        this.fieldInputs = FIELD_INPUTS
        this.dateFormats = DATE_PICKER_FORMATS
        this.datePlaceholder = DATE_PLACEHOLDER

        this.optionsCustom = CUSTOM_OPTIONS
    },
    mounted () {
        this.initialize()
    },
    methods: {
        initialize () {
            const { type, options } = this.value || {}
            this.currentValue = this.normalizeCurrentValue({ type, value: this.defaultValue })
            this.inputValue = this.isDate ? toPickerDate(this.defaultValue) : this.defaultValue
            this.option = ''
            this.conflictChoice = ''
            this.search = ''
            this.searchConfig = (this.isSelect && options.find(({ value: v }) => v === CUSTOM_OPTIONS.SEARCH)) || {}
        },
        handleInputChange (value) {
            this.inputValue = value
            this.notifyInput(this.isDate ? toApiDate(value) : value)
        },
        handleSearchChange (value) {
            this.search = value
            this.notifyInput(value)
        },
        handleOptionChange (option) {
            this.option = option
            this.inputValue = option

            if ([CUSTOM_OPTIONS.SEARCH].includes(option)) {
                this.getSearchOptions()
            } else {
                this.notifyInput(option)
                this.search = ''
                this.searchOptions = []
            }
        },
        handleConflictChange (choice) {
            this.conflictChoice = choice
            const { field, row, saved, new: imported } = this.value
            this.$emit('input', {
                ...this.value,
                resolve: {
                    _inputRowId: row,
                    [field]: choice === 'saved' ? saved : imported,
                },
            })
        },
        formatValue (value) {
            if (value === null || value === undefined || value === '') {
                return '(empty)'
            }
            return Array.isArray(value) ? value.join(', ') : value
        },
        notifyInput (value) {
            const { field, row } = this.value
            this.$emit('input', {
                ...this.value,
                resolve: {
                    _inputRowId: row,
                    [field]: this.search || value,
                    ...(ENABLE_NORMALIZATION ? { mappers: this.mappers } : {}),
                },
            })
        },
        normalizeOptions (data) {
            return (data || []).map(({ label, value }) => ({ key: value, label }))
        },
        normalizeCurrentValue ({ type, value }) {
            if (!value) return value
            if (FIELD_INPUTS[type] === INPUTS.SELECT && Array.isArray(value)) return value.join(', ')
            if (this.isDate) return formatDate(value)
            return value
        },
        async getSearchOptions (search = '') {
            this.isSearching = true
            const { endpoint } = this.searchConfig
            if (endpoint) {
                apiRequest(Api().entities.search(endpoint, { search }))
                    .then(({ data }) => (this.searchOptions = this.normalizeOptions(data)))
                    .catch(rethrowError(notifyError()))
                    .finally(() => setTimeout(() => (this.isSearching = false), 500))
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.WInput, .ImportNotification {
    width: 100%;
}

.Input {
    width: 50%;
}

.Option {
    & + .Option {
        margin-top: 10px;
    }

    &.Separator {
        &:before {
            content: '';
            display: block;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid $gray-05;
            width: 100%;
        }
    }
}
</style>
